import {
	AEMContent,
	Form,
	Heading,
	Modal,
	PrimaryButton,
} from '@mlc/web-ui-toolkit';
import React, { Fragment, FunctionComponent, useRef, useState } from 'react';
import styled from 'styled-components';
import { showError } from '../../../../events/NotificationEmitter';
import {
	EpiAccount,
	FinancialPlanningSoftware,
} from '../../../../logic/domains/epi.domain';
import { createAccount } from '../../../../logic/functions/epiAccounts.function';
import {
	addFormEvent,
	EVENT_FORM_COMPLETED,
} from '../../../../logic/services/digital-data.service';
import { isCsr } from '../../../common/utils';
import ToastContainer from '../../../containers/Notifications/ToastContainer';
import { SomethingWentWrong } from '../../Common/Text/SomethingWentWrong';
import HiddenUsername from '../Common/HiddenUsername';
import Password from '../Password/Password';
import PasswordForm from '../Password/PasswordForm';
import Products from '../Products/Products';
import ProductsForm from '../Products/ProductsForm';
import TermsAndConditions from '../TermsAndConditions/TermsAndConditions';
import Username from '../Username/Username';
import { toFPSMapper } from '../utils';
import { useAdviserDetails } from '../../../hooks/AppStateHook';
import AccountTestIds from './Account.elements';

type Props = {
	token: string;
	fpsOptions: FinancialPlanningSoftware[];
	onComplete: (account: EpiAccount) => void;
};

const CreateAccount: FunctionComponent<Props> = ({
	token,
	fpsOptions,
	onComplete,
}) => {
	const FORM_ID = 'epiForm';
	const [isProcessing, setIsProcessing] = useState(false);
	const [isTCAccepted, setIsTCAccepted] = useState(false);
	const [password, setPassword] = useState<string>('');
	const [epiId, setEpiId] = useState('');
	const [isCriteriaMet, setIsCriteriaMet] = useState(true);
	const [fpsSelected, setFpsSelected] = useState<string[] | undefined>(
		undefined
	);
	const [passwordValid, setPasswordValid] = useState(true);
	const [fpsValid, setFpsValid] = useState(true);
	const [tcValid, setTCValid] = useState(true);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const actionButtonRef = useRef<any>();

	const { roles } = useAdviserDetails();

	const toFps = toFPSMapper(fpsOptions);

	const registerAccount = async () => {
		if (validate()) {
			setIsProcessing(true);
			const epiId = await createAccount(
				token,
				password,
				fpsSelected!.map(toFps)
			);
			setIsProcessing(false);
			if (epiId) {
				setEpiId(epiId);
				addFormEvent(EVENT_FORM_COMPLETED, FORM_ID);
				setShowConfirmation(true);
			} else {
				showError(<SomethingWentWrong />, focus);
				focus();
			}
		}
	};

	const validate = () => {
		const fpsValid = fpsSelected !== undefined && fpsSelected.length > 0;
		setTCValid(isTCAccepted);
		setFpsValid(fpsValid);

		const isPassValid = password.length > 0 && isCriteriaMet;
		setPasswordValid(isPassValid);

		return fpsValid && isTCAccepted && isPassValid;
	};

	const passwordHandler = (password: string, isCriteriaMet: boolean) => {
		setPassword(password);
		setIsCriteriaMet(isCriteriaMet);
		setPasswordValid(true);
	};

	const onCloseConfirmation = () => {
		setShowConfirmation(false);
		onComplete({
			id: epiId,
			isActive: true,
			fps: fpsSelected!.map(toFps),
		});
	};

	const focus = () => {
		if (actionButtonRef.current) {
			actionButtonRef.current.focus();
		}
	};

	return (
		<Fragment>
			<Form id={FORM_ID} onSubmit={(e: any) => e.preventDefault()}>
				<ToastContainer />
				<Username mode="view">
					<div>
						<AEMContent
							path="/secure/_content/Adviser/epi/user-name"
							style={{ marginRight: '30px' }}
						/>
					</div>
					<div />
				</Username>
				<HiddenUsername />
				<Password mode="edit">
					<PasswordForm
						mode={'create'}
						inputHandler={passwordHandler}
						isCriteriaMet={isCriteriaMet}
						isInvalid={!passwordValid}
					/>
				</Password>
				<Products mode="edit">
					<ProductsForm
						fpsOptions={fpsOptions}
						isInvalid={!fpsValid}
						onCheckedHandler={setFpsSelected}
					/>
				</Products>
				<TermsAndConditions
					isAccepted={setIsTCAccepted}
					isInvalid={!tcValid}
					name={'create'}
					mode="edit"
				/>
				<RegisterButton
					isLoading={isProcessing}
					onClick={registerAccount}
					data-id={AccountTestIds.registerButton}
					disabled={isCsr(roles)}
					ref={actionButtonRef}
				>
					Register
				</RegisterButton>
			</Form>
			<ConfirmationModal
				isOpen={showConfirmation}
				onClose={onCloseConfirmation}
			/>
		</Fragment>
	);
};

type ModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

const ConfirmationModal: FunctionComponent<ModalProps> = ({
	isOpen,
	onClose,
}) => (
	<Modal
		isOpen={isOpen}
		onRequestClose={onClose}
		title="Success"
		data-analytics-group="dialog"
	>
		<Heading el="h2" fontSize="little" fontWeight="medium" marginBottom={6}>
			Your Client Data Exchange is registered
		</Heading>
		<AEMContent path="/secure/_content/Adviser/epi/registered-account" />
	</Modal>
);

const RegisterButton = styled(PrimaryButton)`
	display: block;
	margin: 0 auto;
	width: 190px;
`;

export default CreateAccount;
